import {
  Component,
  DoCheck,
  Input,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import type { NotificationModalComponent } from '../../notification-modal.component';
import type { OrderRequestExpandedPackage } from '../../models/notification-order-request.models';
import { OrderRequestPrepaidCardsComponent } from '../order-request-prepaid-cards/order-request-prepaid-cards.component';

/** Gradient for a single wash package that has no theme colour of its own. */
const DEFAULT_SINGLE_WASH_THEME = 'linear-gradient(47deg, #B0325E 3.53%, #FAA681 95.41%)';

export type OrderRequestPackageTabKey = 'single' | 'monthly' | 'prepaid';

export interface OrderRequestPackageTab {
  key: OrderRequestPackageTabKey;
  label: string;
  /** Only Prepaid disables when empty; Single / Monthly stay switchable and show an empty message (CWS-1588). */
  disabled: boolean;
}

/**
 * Packages sub-section of the order-request services form: a tab per wash type (Single Wash,
 * Monthly Wash and, when `includePrepaid` is set, Prepaid), showing only the active tab's cards. Shared by the order-request and fleet-vehicle-found layouts, which
 * render the same cards against the same host state and differ only in the radio group
 * name and the custom-price input id prefix.
 *
 * Logic stays on {@link NotificationModalComponent}; this component only holds the markup.
 */
@Component({
  selector: 'app-order-request-packages-block',
  standalone: true,
  imports: [CommonModule, FormsModule, OrderRequestPrepaidCardsComponent],
  templateUrl: './order-request-packages-block.component.html',
  styleUrls: ['./order-request-packages-block.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class OrderRequestPackagesBlockComponent implements DoCheck {
  /** Owning modal — all state and handlers are read straight off it, as in the layouts. */
  @Input({ required: true }) host!: NotificationModalComponent;

  /** `name` for the package radio inputs; must be unique per rendered layout. */
  @Input({ required: true }) radioName!: string;

  /** Prefix for the single-wash custom-price input ids; must be unique per rendered layout. */
  @Input({ required: true }) customPriceIdPrefix!: string;

  /** Adds the Prepaid wash-book tab; the fleet layout renders its own prepaid section instead. */
  @Input() includePrepaid = false;

  /** The tab the attendant tapped; null until they tap one. */
  private chosenTab: OrderRequestPackageTabKey | null = null;
  private lastSelectionTab: OrderRequestPackageTabKey | null = null;

  /** Rebuilt once per change-detection pass in {@link ngDoCheck}, not on every read. */
  tabs: OrderRequestPackageTab[] = [];

  private buildTabs(): OrderRequestPackageTab[] {
    const tabs: OrderRequestPackageTab[] = [
      { key: 'single', label: 'Single Wash', disabled: false },
      { key: 'monthly', label: 'Monthly Wash', disabled: false },
    ];
    if (this.includePrepaid) {
      tabs.push({
        key: 'prepaid',
        label: 'Prepaid',
        disabled: this.isPrepaidTabDisabled(),
      });
    }
    return tabs;
  }

  /**
   * The tapped tab, otherwise the first tab with cards, otherwise the first enabled tab.
   * Once data has landed the first tab with cards is remembered in `chosenTab` (see
   * {@link ngDoCheck}), so a later catalog arriving does not move the attendant.
   */
  get activeTab(): OrderRequestPackageTabKey | null {
    const tabs = this.tabs;
    const chosen = tabs.find(tab => tab.key === this.chosenTab && !tab.disabled);
    const withContent = tabs.find(tab => !tab.disabled && this.hasContent(tab.key));
    return (chosen ?? withContent ?? tabs.find(tab => !tab.disabled))?.key ?? null;
  }

  /**
   * Single / Monthly say "none" only once the package load finished cleanly. Before that (or
   * after an error) an empty list is not an answer, so staff are not told the shop has no packages.
   */
  showPackagesEmptyMessage(key: 'single' | 'monthly'): boolean {
    const list = key === 'single' ? this.host.singleWashPackages : this.host.monthlyPackages;
    return list.length === 0 && this.host.packagesLoadSettled;
  }

  /** " at <location>" once the attendant's location is known, so the message names the shop. */
  private locationSuffix(): string {
    const form = this.host.formData;
    const id = String(form?.locationId ?? '').trim();
    const name =
      String(form?.location ?? '').trim() ||
      this.host.availableLocationsForDropdown?.find(option => option.id === id)?.name?.trim();
    return name ? ` at ${name}` : '';
  }

  /** Shown in place of the cards when the location has none of that wash type. */
  emptyMessage(key: OrderRequestPackageTabKey): string {
    const where = this.locationSuffix();
    if (key === 'prepaid') {
      return this.host.orderRequestPrepaidLoadFailed
        ? `Prepaid wash books could not be loaded${where}. Please try again.`
        : `No prepaid wash books are available${where}.`;
    }
    if (this.host.packagesLoadFailed) {
      return `Packages could not be loaded${where}. Please try again.`;
    }
    const label = key === 'single' ? 'single wash' : 'monthly wash';
    return `No ${label} packages are available${where}.`;
  }

  /**
   * Prepaid stays locked when it is hidden or frozen on purpose (redemption panel showing the
   * book, fleet order). An empty catalog is not one of those, so the tab opens and says so.
   */
  private isPrepaidTabDisabled(): boolean {
    if (this.host.isOrderRequestPrepaidDisabled()) return true;
    return (
      !this.host.showOrderRequestPrepaidSection() && this.host.orderRequestPrepaidBooks.length > 0
    );
  }

  showPrepaidEmptyMessage(): boolean {
    return (
      !this.host.isLoadingOrderRequestPrepaid && this.host.orderRequestPrepaidBooks.length === 0
    );
  }

  private hasContent(key: OrderRequestPackageTabKey): boolean {
    if (key === 'single') return this.host.singleWashPackages.length > 0;
    if (key === 'monthly') return this.host.monthlyPackages.length > 0;
    return this.host.orderRequestPrepaidBooks.length > 0;
  }

  /** The package's own theme colour; single wash tiles without one fall back to the default gradient. */
  cardTheme(expandedPackage: OrderRequestExpandedPackage, monthly: boolean): string | null {
    return (
      this.host.themeColorForPackage(expandedPackage.package) ??
      (monthly ? null : DEFAULT_SINGLE_WASH_THEME)
    );
  }

  /** `tabs` is rebuilt every pass; keying on `key` keeps the buttons in the DOM. */
  trackTab(_index: number, tab: OrderRequestPackageTab): OrderRequestPackageTabKey {
    return tab.key;
  }

  selectTab(tab: OrderRequestPackageTab): void {
    if (!tab.disabled) {
      this.chosenTab = tab.key;
    }
  }

  /** A selection made elsewhere (e.g. a Most used service tile) brings its tab to the front. */
  ngDoCheck(): void {
    if (!this.host) {
      return;
    }
    this.tabs = this.buildTabs();
    this.rememberFirstTabWithContent();
    const selectionTab = this.selectionTab();
    if (selectionTab !== this.lastSelectionTab) {
      this.lastSelectionTab = selectionTab;
      if (selectionTab) {
        this.chosenTab = selectionTab;
      }
    }
  }

  /** Pins the first tab with cards once loading is over, so later arrivals cannot switch tabs. */
  private rememberFirstTabWithContent(): void {
    if (this.chosenTab || this.host.isLoadingPackages) {
      return;
    }
    const first = this.tabs.find(tab => !tab.disabled && this.hasContent(tab.key));
    if (first) {
      this.chosenTab = first.key;
    }
  }

  private selectionTab(): OrderRequestPackageTabKey | null {
    if (this.host.singleWashPackages.some(p => this.host.isExpandedPackageSelected(p))) {
      return 'single';
    }
    if (this.host.monthlyPackages.some(p => this.host.isExpandedPackageSelected(p))) {
      return 'monthly';
    }
    return this.includePrepaid && this.host.orderRequestSelectedPrepaidBookId ? 'prepaid' : null;
  }

  /**
   * Enter / Space on a focused tile selects it, like a click. Keys pressed on a control nested
   * in the tile (the custom-price pill, edit button, amount input) are left to that control.
   */
  onKeyboardSelect(event: Event, expandedPackage: OrderRequestExpandedPackage): void {
    if (event.target !== event.currentTarget) {
      return;
    }
    event.preventDefault();
    this.host.onExpandedPackageSelect(expandedPackage);
  }
}
